import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { FileSpreadsheet, FileText, Loader2, Pencil, Trash2 } from 'lucide-react';
import {
  AcademicCalendarEvent,
  AcademicCalendarTerm,
  AcademicCalendarType,
  AcademicCalendarWriteData,
  ACADEMIC_CALENDAR_TERMS,
  ACADEMIC_CALENDAR_TYPES,
} from '@/services/academic.api';
import { OrgReportsApi } from '@/services/org-reports.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, useDataTable } from '@/components/data-table';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const columnHelper = createColumnHelper<AcademicCalendarEvent>();

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

interface EventFormValues {
  name: string;
  description: string;
  type: AcademicCalendarType | '';
  date: string;
  term: AcademicCalendarTerm | '';
  academicYearId: string;
}

const EMPTY_FORM: EventFormValues = { name: '', description: '', type: '', date: '', term: '', academicYearId: '' };

function toWriteData(v: EventFormValues): AcademicCalendarWriteData {
  return {
    name: v.name.trim(),
    description: v.description.trim() || undefined,
    type: v.type as AcademicCalendarType,
    date: v.date,
    term: v.term || undefined,
    academicYearId: v.academicYearId || undefined,
  };
}

/**
 * Academic Calendar — org/superadmin tier. Matches real legacy's single
 * `AcademicCalendarController` EXACTLY (see docs/DEVIATIONS.md): no school
 * selector, no school column — a single flat calendar, same filters
 * (Session Year/Term/Month/Type) and same columns as the school-tier page
 * this is modeled on. Reads/writes go through the caller's "master" school
 * (the oldest school in scope); every write (create/edit/delete/generate)
 * is fanned out server-side to every other school in scope — this page has
 * no UI concept of "which school," by design, matching legacy's own single
 * master-calendar screen.
 */
export function OrgAcademicCalendarPage() {
  const qc = useQueryClient();
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'date', defaultSortOrder: 'asc' });

  const [yearId, setYearId] = useState('');
  const [term, setTerm] = useState('');
  const [month, setMonth] = useState<number | ''>('');
  const [type, setType] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: defaultYear } = useQuery({
    queryKey: ['org-academic-calendar', 'default-year'],
    queryFn: OrgReportsApi.academicCalendarDefaultYear,
  });
  const { data: years = [] } = useQuery({
    queryKey: ['org-academic-calendar', 'years'],
    queryFn: OrgReportsApi.academicCalendarYears,
  });

  useEffect(() => {
    if (defaultYear?.id) setYearId((y) => y || defaultYear.id);
  }, [defaultYear?.id]);

  // Changing Session Year/Term/Month/Type narrows the result set — without
  // this, staying on a later page after switching filters can show an
  // empty table (looks like the filter silently returned nothing, when
  // it's really just a stale page number). Same fix applied to the
  // school-tier AcademicCalendarPage.tsx.
  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearId, term, month, type]);

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['org-academic-calendar', 'list', dt.params, yearId, term, month, type],
    queryFn: () =>
      OrgReportsApi.academicCalendar({
        ...dt.params,
        academicYearId: yearId || undefined,
        term: term || undefined,
        month: month || undefined,
        type: type || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ['org-academic-calendar'] });
  }

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportAcademicCalendar(format, {
        academicYearId: yearId || undefined,
        term: term || undefined,
        month: month || undefined,
        type: type || undefined,
        search: dt.params.search,
      });
    } finally {
      setBusy(null);
    }
  };

  // ── Create form (inline, above the list — matching the school-tier
  // "Create Academic Calendar For A Day" layout, which itself matches
  // real legacy's layout) ──
  const [form, setForm] = useState<EventFormValues>(EMPTY_FORM);
  const create = useMutation({
    mutationFn: () => OrgReportsApi.createAcademicCalendar(toWriteData(form)),
    onSuccess: () => {
      setForm(EMPTY_FORM);
      invalidateAll();
    },
  });

  // ── Edit modal ──
  const [editing, setEditing] = useState<AcademicCalendarEvent | null>(null);
  const [editForm, setEditForm] = useState<EventFormValues>(EMPTY_FORM);
  useEffect(() => {
    if (editing) {
      setEditForm({
        name: editing.name,
        description: editing.description ?? '',
        type: (editing.type as AcademicCalendarType) ?? '',
        date: editing.date,
        term: (editing.term as AcademicCalendarTerm) ?? '',
        academicYearId: editing.academicYearId ?? '',
      });
    }
  }, [editing]);
  const update = useMutation({
    mutationFn: () => OrgReportsApi.updateAcademicCalendar(editing!.id, toWriteData(editForm)),
    onSuccess: () => {
      setEditing(null);
      invalidateAll();
    },
  });

  // ── Delete confirm ──
  const [deleting, setDeleting] = useState<AcademicCalendarEvent | null>(null);
  const remove = useMutation({
    mutationFn: () => OrgReportsApi.removeAcademicCalendar(deleting!.id),
    onSuccess: () => {
      setDeleting(null);
      invalidateAll();
    },
  });

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('name', { header: 'Name' }),
    columnHelper.accessor('type', {
      header: 'Type',
      cell: (ctx) => (
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ctx.row.original.color || undefined }} />
          {ctx.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor('term', { header: 'Term', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('date', { header: 'Date' }),
    columnHelper.accessor('academicYearName', { header: 'Academic Year', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: (ctx) => (
        <span className="inline-flex items-center gap-2">
          <button
            type="button"
            title="Edit"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"
            onClick={() => setEditing(ctx.row.original)}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Delete"
            className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
            onClick={() => setDeleting(ctx.row.original)}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </span>
      ),
    }),
  ];

  return (
    <>
      <PageHeader
        title="Academic Calendar"
        description="Exams, teacher trainings, and other calendar events. Every create/edit/delete/generate here applies across every school in your organization."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      {/* Create Academic Calendar For A Day */}
      <div className="card mb-6 p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">Create Academic Calendar For A Day</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="Description">
            <Textarea
              rows={1}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Field>
          <Field label="Type" required>
            <Select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as AcademicCalendarType }))}>
              <option value="">Select Type</option>
              {ACADEMIC_CALENDAR_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date" required>
            <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
          </Field>
          <Field label="Term">
            <Select value={form.term} onChange={(e) => setForm((f) => ({ ...f, term: e.target.value as AcademicCalendarTerm }))}>
              <option value="">Select Term</option>
              {ACADEMIC_CALENDAR_TERMS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Session Year">
            <Select value={form.academicYearId} onChange={(e) => setForm((f) => ({ ...f, academicYearId: e.target.value }))}>
              <option value="">Select Session Year</option>
              {years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="mt-3">
          <button
            className="btn-primary"
            disabled={!form.name.trim() || !form.type || !form.date || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Saving…' : 'Submit'}
          </button>
        </div>
        {create.isSuccess && (
          <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">✓ Calendar event created.</div>
        )}
        {create.error && (
          <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(create.error)}</div>
        )}
      </div>

      {/* Generate Academic Calendar For A Month */}
      <GenerateCalendarCard years={years} onGenerated={invalidateAll} />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-44" value={yearId} onChange={(e) => setYearId(e.target.value)}>
          <option value="">All Years</option>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
        <Select className="!w-32" value={term} onChange={(e) => setTerm(e.target.value)}>
          <option value="">All Terms</option>
          {ACADEMIC_CALENDAR_TERMS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
        <Select
          className="!w-36"
          value={month}
          onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : '')}
        >
          <option value="">All Months</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>
              {m}
            </option>
          ))}
        </Select>
        <Select className="!w-44" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All Types</option>
          {ACADEMIC_CALENDAR_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={columns}
        result={pageData}
        state={dt}
        loading={isFetching}
        emptyMessage="No calendar events for the selected filters."
        getRowId={(row) => row.id}
      />

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Calendar Event" size="lg">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Name" required>
            <Input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="Description">
            <Textarea
              rows={1}
              value={editForm.description}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Field>
          <Field label="Type" required>
            <Select
              value={editForm.type}
              onChange={(e) => setEditForm((f) => ({ ...f, type: e.target.value as AcademicCalendarType }))}
            >
              <option value="">Select Type</option>
              {ACADEMIC_CALENDAR_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date" required>
            <Input
              type="date"
              value={editForm.date}
              onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))}
            />
          </Field>
          <Field label="Term">
            <Select
              value={editForm.term}
              onChange={(e) => setEditForm((f) => ({ ...f, term: e.target.value as AcademicCalendarTerm }))}
            >
              <option value="">Select Term</option>
              {ACADEMIC_CALENDAR_TERMS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Session Year">
            <Select
              value={editForm.academicYearId}
              onChange={(e) => setEditForm((f) => ({ ...f, academicYearId: e.target.value }))}
            >
              <option value="">Select Session Year</option>
              {years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {update.error && (
          <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(update.error)}</div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-secondary" onClick={() => setEditing(null)}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!editForm.name.trim() || !editForm.type || !editForm.date || update.isPending}
            onClick={() => update.mutate()}
          >
            {update.isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate()}
        title="Delete calendar event?"
        message={`This will permanently delete "${deleting?.name}" across every school in your organization. This cannot be undone.`}
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}

/** "Generate Academic Calendar For A Month" — fans out to every school in scope server-side; see OrgReportsService.generateAcademicCalendar()'s doc comment. */
function GenerateCalendarCard({ years, onGenerated }: { years: { id: string; name: string }[]; onGenerated: () => void }) {
  const [academicYearId, setAcademicYearId] = useState('');
  const [months, setMonths] = useState<Set<number>>(new Set());
  const [term, setTerm] = useState<AcademicCalendarTerm | ''>('');

  const generate = useMutation({
    mutationFn: () =>
      OrgReportsApi.generateAcademicCalendar({ months: [...months], term: term as AcademicCalendarTerm, academicYearId }),
    onSuccess: () => {
      setMonths(new Set());
      onGenerated();
    },
  });

  function toggleMonth(m: number) {
    setMonths((prev) => {
      const next = new Set(prev);
      next.has(m) ? next.delete(m) : next.add(m);
      return next;
    });
  }

  const valid = !!academicYearId && months.size > 0 && !!term;

  return (
    <div className="card mb-6 p-4">
      <h3 className="mb-1 text-sm font-semibold text-slate-700">Generate Academic Calendar For A Month</h3>
      <p className="mb-3 text-xs text-slate-500">
        Creates a "Working Day" entry for every Sunday in the selected month(s), across every school in your organization. Already-existing dates are left untouched — safe to run again.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Session Year" required>
          <Select value={academicYearId} onChange={(e) => setAcademicYearId(e.target.value)}>
            <option value="">Select Session Year</option>
            {years.map((y) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Term" required>
          <Select value={term} onChange={(e) => setTerm(e.target.value as AcademicCalendarTerm)}>
            <option value="">Select Term</option>
            {ACADEMIC_CALENDAR_TERMS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="mt-3">
        <span className="mb-1.5 block text-sm font-medium text-slate-700">
          Month{months.size > 0 ? ` (${months.size} selected)` : ''} <span className="text-red-500">*</span>
        </span>
        <div className="flex flex-wrap gap-2">
          {MONTHS.map((m, i) => {
            const num = i + 1;
            const checked = months.has(num);
            return (
              <label
                key={m}
                className={`cursor-pointer rounded-md border px-2.5 py-1 text-sm transition ${
                  checked ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <input type="checkbox" checked={checked} onChange={() => toggleMonth(num)} className="sr-only" />
                {m.slice(0, 3)}
              </label>
            );
          })}
        </div>
      </div>
      <div className="mt-3">
        <button className="btn-primary" disabled={!valid || generate.isPending} onClick={() => generate.mutate()}>
          {generate.isPending ? 'Generating…' : 'Submit'}
        </button>
      </div>
      {generate.data && (
        <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
          ✓ Created {generate.data.created} working day{generate.data.created === 1 ? '' : 's'}
          {generate.data.skipped > 0 ? ` (${generate.data.skipped} already existed, left unchanged)` : ''}.
        </div>
      )}
      {generate.error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(generate.error)}</div>
      )}
    </div>
  );
}
